import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import enterpriseSetupApi from "../services/enterpriseSetupApi";

const ACCENT = "#2DB838";
const ACCENT_DARK = "#0E6346";

const emptyTenant = {
  code: "",
  company_name: "",
  support_email: "",
  timezone: "Africa/Harare",
  brand_primary: ACCENT,
  brand_secondary: ACCENT_DARK,
  allowed_email_domains: [],
  domains: [],
  settings: {
    sso_required: false,
    auto_provision_users: false,
  },
  is_setup_complete: false,
  is_default: false,
};

function SectionCard({ title, subtitle, children, action }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid rgba(45,184,56,0.15)",
        bgcolor: "rgba(255,255,255,0.88)",
        boxShadow: "0 18px 38px -30px rgba(14,99,70,0.28)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={900}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

const normalizeProviderForForm = (provider) => ({
  ...provider,
  config_json: JSON.stringify(provider?.config || {}, null, 2),
});

export default function EnterpriseSetupPage() {
  const [loading, setLoading] = useState(true);
  const [savingTenant, setSavingTenant] = useState(false);
  const [savingProviderId, setSavingProviderId] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [payload, setPayload] = useState(null);
  const [tenant, setTenant] = useState(emptyTenant);
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedTenantCode, setSelectedTenantCode] = useState("");
  const [newTenant, setNewTenant] = useState({
    code: "",
    company_name: "",
    support_email: "",
    timezone: "Africa/Harare",
    brand_primary: ACCENT,
    brand_secondary: ACCENT_DARK,
    domains: "",
  });

  const selectedUser = useMemo(
    () => users.find((candidate) => String(candidate.id) === String(selectedUserId)) || null,
    [selectedUserId, users]
  );

  const tenantOptions = Array.isArray(payload?.tenants) ? payload.tenants : [];
  const readiness = payload?.readiness;
  const permissionCatalog = Array.isArray(payload?.permission_catalog) ? payload.permission_catalog : [];

  const loadSetup = async (tenantCode = selectedTenantCode) => {
    setLoading(true);
    setError("");

    try {
      const response = await enterpriseSetupApi.getSetup(tenantCode);
      const nextPayload = response?.data || null;
      setPayload(nextPayload);
      setTenant(nextPayload?.tenant_profile || emptyTenant);
      setProviders(
        (Array.isArray(nextPayload?.identity_providers) ? nextPayload.identity_providers : []).map(
          normalizeProviderForForm
        )
      );

      const nextUsers = Array.isArray(nextPayload?.users) ? nextPayload.users : [];
      setUsers(nextUsers);
      setSelectedUserId((current) => current || String(nextUsers[0]?.id || ""));
      setSelectedTenantCode(nextPayload?.tenant_profile?.code || tenantCode || "");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load enterprise setup.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSetup("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveTenant = async () => {
    setSavingTenant(true);
    setError("");

    try {
      const response = await enterpriseSetupApi.updateTenantProfile(
        {
          ...tenant,
          allowed_email_domains: String(tenant.allowed_email_domains || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
          domains: String(tenant.domains || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
        },
        selectedTenantCode
      );

      setMessage(response?.message || "Tenant profile updated.");
      await loadSetup(selectedTenantCode);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to update enterprise tenant profile.");
    } finally {
      setSavingTenant(false);
    }
  };

  const handleCreateTenant = async () => {
    setCreatingTenant(true);
    setError("");

    try {
      const response = await enterpriseSetupApi.createTenant({
        code: newTenant.code,
        company_name: newTenant.company_name,
        support_email: newTenant.support_email || null,
        timezone: newTenant.timezone || "Africa/Harare",
        brand_primary: newTenant.brand_primary || ACCENT,
        brand_secondary: newTenant.brand_secondary || ACCENT_DARK,
        domains: String(newTenant.domains || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      });

      const nextCode = response?.data?.code || "";
      setMessage(response?.message || "Tenant profile created.");
      setNewTenant({
        code: "",
        company_name: "",
        support_email: "",
        timezone: "Africa/Harare",
        brand_primary: ACCENT,
        brand_secondary: ACCENT_DARK,
        domains: "",
      });
      await loadSetup(nextCode);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to create tenant profile.");
    } finally {
      setCreatingTenant(false);
    }
  };

  const handleSaveProvider = async (provider) => {
    setSavingProviderId(provider.id);
    setError("");

    try {
      const response = await enterpriseSetupApi.saveIdentityProvider(
        {
          id: provider.id,
          slug: provider.slug,
          name: provider.name,
          is_enabled: provider.is_enabled,
          config: JSON.parse(provider.config_json || "{}"),
        },
        selectedTenantCode
      );

      setMessage(response?.message || "Identity provider updated.");
      await loadSetup(selectedTenantCode);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save identity provider.");
    } finally {
      setSavingProviderId(null);
    }
  };

  const handleTogglePermission = (permission) => {
    if (!selectedUser) return;

    const resolved = new Set(selectedUser.permissions?.resolved || []);
    const overrides = { ...(selectedUser.permissions?.overrides || {}) };
    const currentlyAllowed = resolved.has(permission);
    overrides[permission] = !currentlyAllowed;

    setUsers((prev) =>
      prev.map((candidate) =>
        candidate.id === selectedUser.id
          ? {
              ...candidate,
              permissions: {
                ...candidate.permissions,
                overrides,
                resolved: currentlyAllowed
                  ? (candidate.permissions?.resolved || []).filter((item) => item !== permission)
                  : [...new Set([...(candidate.permissions?.resolved || []), permission])],
              },
            }
          : candidate
      )
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    setSavingPermissions(true);
    setError("");

    try {
      const response = await enterpriseSetupApi.updateUserPermissions(
        selectedUser.id,
        selectedUser.permissions?.overrides || {},
        selectedTenantCode
      );

      setUsers((prev) =>
        prev.map((candidate) => (candidate.id === selectedUser.id ? response?.data || candidate : candidate))
      );
      setMessage(response?.message || "User permissions updated.");
      await loadSetup(selectedTenantCode);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to update user permissions.");
    } finally {
      setSavingPermissions(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(45,184,56,0.16), transparent 28%), linear-gradient(180deg, #f4faf5 0%, #eef4ef 100%)",
      }}
    >
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 5,
            border: "1px solid rgba(45,184,56,0.18)",
            bgcolor: "rgba(255,255,255,0.84)",
            boxShadow: "0 24px 50px -36px rgba(14,99,70,0.34)",
          }}
        >
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <Chip
                  label={readiness?.launch_ready ? "Launch Ready" : "Setup In Progress"}
                  sx={{
                    bgcolor: readiness?.launch_ready ? alpha(ACCENT, 0.14) : "rgba(255,186,8,0.16)",
                    color: readiness?.launch_ready ? ACCENT_DARK : "#8a5b00",
                    fontWeight: 900,
                  }}
                />
                <Chip
                  icon={<ShieldRoundedIcon sx={{ color: ACCENT }} />}
                  label="Enterprise Control Plane"
                  variant="outlined"
                  sx={{ borderColor: alpha(ACCENT, 0.2), color: ACCENT_DARK }}
                />
              </Stack>
              <Typography variant="h4" fontWeight={900}>
                Enterprise Setup
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.8, maxWidth: 860 }}>
                Configure tenant branding, SSO, access policy, and launch readiness from one sellable onboarding surface.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
              <Chip label={`Ready: ${readiness?.summary?.ready_count || 0}`} sx={{ bgcolor: alpha(ACCENT, 0.12), color: ACCENT_DARK, fontWeight: 800 }} />
              <Chip label={`Warnings: ${readiness?.summary?.warning_count || 0}`} sx={{ bgcolor: "rgba(255,186,8,0.16)", color: "#8a5b00", fontWeight: 800 }} />
              <Chip label={`Blocking: ${readiness?.summary?.blocking_count || 0}`} sx={{ bgcolor: "rgba(211,47,47,0.12)", color: "#8d1f1f", fontWeight: 800 }} />
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}

        {loading ? (
          <Alert severity="info">Loading enterprise setup...</Alert>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <SectionCard
                title="Tenant Setup Wizard"
                subtitle="Select the active tenant you are configuring, or create a new branded tenant profile for a separate commercial rollout."
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Active tenant"
                      value={selectedTenantCode}
                      onChange={(event) => {
                        const nextCode = event.target.value;
                        setSelectedTenantCode(nextCode);
                        loadSetup(nextCode);
                      }}
                      fullWidth
                    >
                      {tenantOptions.map((tenantOption) => (
                        <MenuItem key={tenantOption.code} value={tenantOption.code}>
                          {tenantOption.company_name} - {tenantOption.code}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip label={`Code: ${tenant.code || "default"}`} variant="outlined" />
                      <Chip label={tenant.is_default ? "Default tenant" : "Secondary tenant"} sx={{ bgcolor: tenant.is_default ? alpha(ACCENT, 0.12) : "rgba(15,23,42,0.05)", color: tenant.is_default ? ACCENT_DARK : "text.secondary", fontWeight: 800 }} />
                      <Chip label={tenant.is_setup_complete ? "Onboarding complete" : "Onboarding open"} sx={{ bgcolor: tenant.is_setup_complete ? alpha(ACCENT, 0.12) : "rgba(255,186,8,0.16)", color: tenant.is_setup_complete ? ACCENT_DARK : "#8a5b00", fontWeight: 800 }} />
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1.5 }}>
                  Create tenant
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField label="Tenant code" value={newTenant.code} onChange={(e) => setNewTenant((prev) => ({ ...prev, code: e.target.value }))} fullWidth />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Company name" value={newTenant.company_name} onChange={(e) => setNewTenant((prev) => ({ ...prev, company_name: e.target.value }))} fullWidth />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Support email" value={newTenant.support_email} onChange={(e) => setNewTenant((prev) => ({ ...prev, support_email: e.target.value }))} fullWidth />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Domains" placeholder="desk.example.com, app.example.com" value={newTenant.domains} onChange={(e) => setNewTenant((prev) => ({ ...prev, domains: e.target.value }))} fullWidth />
                  </Grid>
                </Grid>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={handleCreateTenant} disabled={creatingTenant || !newTenant.code || !newTenant.company_name} sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_DARK } }}>
                    {creatingTenant ? "Creating..." : "Create Tenant"}
                  </Button>
                </Stack>
              </SectionCard>
            </Grid>

            <Grid item xs={12} xl={7}>
              <Stack spacing={2.5}>
                <SectionCard
                  title="Tenant Profile"
                  subtitle="Branding, allowed domains, SSO posture, and deployment metadata for the selected tenant."
                  action={
                    <Button
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={handleSaveTenant}
                      disabled={savingTenant}
                      sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_DARK } }}
                    >
                      {savingTenant ? "Saving..." : "Save Tenant"}
                    </Button>
                  }
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField label="Company name" value={tenant.company_name || ""} onChange={(e) => setTenant((prev) => ({ ...prev, company_name: e.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Support email" value={tenant.support_email || ""} onChange={(e) => setTenant((prev) => ({ ...prev, support_email: e.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField label="Timezone" value={tenant.timezone || ""} onChange={(e) => setTenant((prev) => ({ ...prev, timezone: e.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField label="Primary brand color" value={tenant.brand_primary || ""} onChange={(e) => setTenant((prev) => ({ ...prev, brand_primary: e.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField label="Secondary brand color" value={tenant.brand_secondary || ""} onChange={(e) => setTenant((prev) => ({ ...prev, brand_secondary: e.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Allowed email domains"
                        placeholder="example.com, subsidiary.co.zw"
                        value={Array.isArray(tenant.allowed_email_domains) ? tenant.allowed_email_domains.join(", ") : tenant.allowed_email_domains || ""}
                        onChange={(e) => setTenant((prev) => ({ ...prev, allowed_email_domains: e.target.value }))}
                        helperText="Optional restriction applied to SSO and user provisioning."
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Tenant hostnames"
                        placeholder="desk.example.com, crm.example.com"
                        value={Array.isArray(tenant.domains) ? tenant.domains.join(", ") : tenant.domains || ""}
                        onChange={(e) => setTenant((prev) => ({ ...prev, domains: e.target.value }))}
                        helperText="If set, public tenant context and SSO routing can resolve from the incoming hostname."
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle2" fontWeight={900}>
                              Require SSO
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Hide local login and force configured identity providers on the login screen.
                            </Typography>
                          </Box>
                          <Switch
                            checked={Boolean(tenant?.settings?.sso_required)}
                            onChange={(e) =>
                              setTenant((prev) => ({
                                ...prev,
                                settings: {
                                  ...(prev.settings || {}),
                                  sso_required: e.target.checked,
                                },
                              }))
                            }
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle2" fontWeight={900}>
                              Auto Provision
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Allow SSO users from approved domains to be created automatically.
                            </Typography>
                          </Box>
                          <Switch
                            checked={Boolean(tenant?.settings?.auto_provision_users)}
                            onChange={(e) =>
                              setTenant((prev) => ({
                                ...prev,
                                settings: {
                                  ...(prev.settings || {}),
                                  auto_provision_users: e.target.checked,
                                },
                              }))
                            }
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                    <Chip icon={<TravelExploreRoundedIcon sx={{ color: ACCENT }} />} label={`Tenant code: ${tenant.code || "default"}`} variant="outlined" />
                    <Chip label={tenant.is_default ? "Default tenant" : "Non-default tenant"} sx={{ bgcolor: tenant.is_default ? alpha(ACCENT, 0.12) : "rgba(15,23,42,0.05)", color: tenant.is_default ? ACCENT_DARK : "text.secondary", fontWeight: 800 }} />
                  </Stack>
                </SectionCard>

                <SectionCard
                  title="Identity Providers"
                  subtitle="Enable Google or SAML per tenant and store the exact provider metadata needed for live enterprise authentication."
                >
                  <Stack spacing={2}>
                    {providers.map((provider) => (
                      <Paper key={provider.id} variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(ACCENT, 0.16) }}>
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
                            <Box>
                              <Typography variant="subtitle1" fontWeight={900}>
                                {provider.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {provider.slug === "google"
                                  ? "Google OAuth login is available immediately when credentials are configured."
                                  : "SAML supports tenant-specific metadata and signed assertion validation."}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={provider.type} variant="outlined" />
                              <Switch
                                checked={Boolean(provider.is_enabled)}
                                onChange={(e) =>
                                  setProviders((prev) =>
                                    prev.map((item) =>
                                      item.id === provider.id ? { ...item, is_enabled: e.target.checked } : item
                                    )
                                  )
                                }
                              />
                            </Stack>
                          </Stack>

                          <TextField
                            label="Provider display name"
                            value={provider.name}
                            onChange={(e) =>
                              setProviders((prev) =>
                                prev.map((item) => (item.id === provider.id ? { ...item, name: e.target.value } : item))
                              )
                            }
                            fullWidth
                          />

                          <TextField
                            label="Provider configuration JSON"
                            value={provider.config_json || "{}"}
                            onChange={(e) =>
                              setProviders((prev) =>
                                prev.map((item) =>
                                  item.id === provider.id ? { ...item, config_json: e.target.value } : item
                                )
                              )
                            }
                            fullWidth
                            multiline
                            minRows={7}
                            helperText={
                              provider.slug === "google"
                                ? "client_id, client_secret, redirect, allowed_domains, auto_provision, default_role"
                                : "sso_url, sp_entity_id, acs_url, x509_certificate, allowed_domains, auto_provision, default_role, email_attribute, name_attribute"
                            }
                          />

                          <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap">
                            {provider.login_url ? <Chip label="Login URL ready" variant="outlined" /> : null}
                            {provider.metadata_url ? <Chip label="Metadata URL available" variant="outlined" /> : null}
                          </Stack>

                          <Stack direction="row" justifyContent="flex-end">
                            <Button
                              variant="contained"
                              onClick={() => handleSaveProvider(provider)}
                              disabled={savingProviderId === provider.id}
                              sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_DARK } }}
                            >
                              {savingProviderId === provider.id ? "Saving..." : "Save Provider"}
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </SectionCard>
              </Stack>
            </Grid>

            <Grid item xs={12} xl={5}>
              <Stack spacing={2.5}>
                <SectionCard
                  title="Permission Overrides"
                  subtitle="Move beyond role-only access with explicit grants and revocations per user."
                  action={
                    <Button
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={handleSavePermissions}
                      disabled={!selectedUser || savingPermissions}
                      sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_DARK } }}
                    >
                      {savingPermissions ? "Saving..." : "Save Permissions"}
                    </Button>
                  }
                >
                  <TextField
                    select
                    label="User"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={String(user.id)}>
                        {user.name} - {user.role}
                      </MenuItem>
                    ))}
                  </TextField>

                  {selectedUser ? (
                    <Stack spacing={1.5}>
                      <Alert severity="info">
                        Role defaults still apply unless explicitly revoked here.
                      </Alert>
                      {Object.entries(
                        permissionCatalog.reduce((groups, permission) => {
                          const key = permission.group || "Other";
                          groups[key] = groups[key] || [];
                          groups[key].push(permission);
                          return groups;
                        }, {})
                      ).map(([group, permissions]) => (
                        <Box key={group}>
                          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                            {group}
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {permissions.map((permission) => {
                              const allowed = selectedUser.permissions?.resolved?.includes(permission.slug);
                              return (
                                <Chip
                                  key={permission.slug}
                                  label={permission.label}
                                  clickable
                                  onClick={() => handleTogglePermission(permission.slug)}
                                  sx={{
                                    bgcolor: allowed ? alpha(ACCENT, 0.14) : "rgba(15,23,42,0.04)",
                                    color: allowed ? ACCENT_DARK : "text.primary",
                                    fontWeight: 800,
                                    border: `1px solid ${allowed ? alpha(ACCENT, 0.22) : "rgba(15,23,42,0.08)"}`,
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Select a user to manage explicit permission overrides.</Alert>
                  )}
                </SectionCard>

                <SectionCard
                  title="Readiness Snapshot"
                  subtitle="Commercial launch gating across routing, SLA, provider credentials, governance, and voice readiness."
                >
                  <Stack spacing={1.25}>
                    {(readiness?.checks || []).map((check) => (
                      <Box key={check.code}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" fontWeight={800}>
                            {check.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={check.status}
                            sx={{
                              bgcolor:
                                check.status === "ready"
                                  ? alpha(ACCENT, 0.14)
                                  : check.status === "warning"
                                    ? "rgba(255,186,8,0.16)"
                                    : "rgba(211,47,47,0.12)",
                              color:
                                check.status === "ready"
                                  ? ACCENT_DARK
                                  : check.status === "warning"
                                    ? "#8a5b00"
                                    : "#8d1f1f",
                              fontWeight: 800,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                          {check.description}
                        </Typography>
                        {check.action ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.45 }}>
                            Next action: {check.action}
                          </Typography>
                        ) : null}
                        <Divider sx={{ mt: 1.2 }} />
                      </Box>
                    ))}
                  </Stack>
                </SectionCard>
              </Stack>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Box>
  );
}
